import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { OnboardingSurveyEndpoint } from '@core/types/types.endpoint.onboarding';
import { Button } from '@frontend/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@frontend/components/ui/card';
import { Label } from '@frontend/components/ui/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@frontend/components/ui/radio-group';
import { DIRECTORY_KEY } from '@frontend/shared/const/const.DIRECTORY_KEY';
import { useBroadcaster } from '@frontend/shared/hooks/shared.hook.useBroadcaster';
import { DirectoryContext } from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

export function AppOnboardingSurvey() {
  const directoryContext = useContext(DirectoryContext);
  const broadcast = useBroadcaster();
  const navigate = useNavigate();
  const [companySize, setCompanySize] = useState('');
  const [role, setRole] = useState('');
  const [knowledgeOwner, setKnowledgeOwner] = useState('');
  const [budgetStatus, setBudgetStatus] = useState('');
  const [decisionMaker, setDecisionMaker] = useState('');

  const submit = useSubmitter(async () => {
    const [error, res] = await callApi<OnboardingSurveyEndpoint>(
      '/onboarding/survey',
      {
        companySize: companySize as
          | '< 100'
          | '100-500'
          | '500-1000'
          | '1000-5000'
          | '5000+',
        role: role as
          | 'Individual contributor'
          | 'Team lead'
          | 'Knowledge manager/enabler'
          | 'IT/Operations',
        knowledgeOwner: knowledgeOwner as
          | 'Me'
          | 'My team'
          | 'Dedicated team'
          | "Don't know / Nobody",
        budgetStatus: budgetStatus as
          | 'Approved'
          | 'In planning'
          | 'No budget yet'
          | 'Unsure',
        decisionMaker: decisionMaker as
          | 'Me'
          | 'Executive leadership (C-suite/VP)'
          | 'Department head/Director'
          | 'Cross-functional committee',
      },
    );

    if (error) {
      console.error('Survey submission error:', error);
      throw error;
    }

    if (res && res.team) {
      // Update directory if context is available, otherwise save to localStorage
      if (directoryContext) {
        // Use broadcaster to update directory if context exists
        broadcast({
          event: 'directory:save',
          directory: { ...directoryContext, team: res.team },
        });
      } else {
        // Save directory updates directly to localStorage for onboarding
        const existingDir = localStorage.getItem(DIRECTORY_KEY);
        const directory = existingDir ? JSON.parse(existingDir) : {};
        const updatedDirectory = { ...directory, team: res.team };
        localStorage.setItem(DIRECTORY_KEY, JSON.stringify(updatedDirectory));

        // Force page reload to ensure App component picks up the updated onboarding_complete flag
        window.location.href = '/questions';
        return;
      }

      // Navigate to questions page after successful submission
      // The App component will handle redirecting based on user role
      navigate('/questions', { replace: true });
    }
  });

  const allFieldsFilled =
    companySize && role && knowledgeOwner && budgetStatus && decisionMaker;

  return (
    <div className="flex flex-col gap-3">
      {/* Question 1: Company size */}
      <Card style={{ opacity: 1 }}>
        <CardHeader>
          <CardTitle>Company size</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup value={companySize} onValueChange={setCompanySize}>
            <div className="flex flex-col gap-0">
              <Label
                htmlFor="size-1"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem value="< 100" id="size-1" />
                <span>&lt; 100</span>
              </Label>
              <Label
                htmlFor="size-2"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem value="100-500" id="size-2" />
                <span>100-500</span>
              </Label>
              <Label
                htmlFor="size-3"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem value="500-1000" id="size-3" />
                <span>500-1000</span>
              </Label>
              <Label
                htmlFor="size-4"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem value="1000-5000" id="size-4" />
                <span>1000-5000</span>
              </Label>
              <Label
                htmlFor="size-5"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem value="5000+" id="size-5" />
                <span>5000+</span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Question 2: Your role */}
      <Card style={{ opacity: companySize ? 1 : 0.3 }}>
        <CardHeader>
          <CardTitle>Your role</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={role}
            onValueChange={setRole}
            disabled={!companySize}
          >
            <div className="flex flex-col gap-0">
              <Label
                htmlFor="role-1"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Individual contributor"
                  id="role-1"
                  disabled={!companySize}
                />
                <span>Individual contributor</span>
              </Label>
              <Label
                htmlFor="role-2"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Team lead"
                  id="role-2"
                  disabled={!companySize}
                />
                <span>Team lead</span>
              </Label>
              <Label
                htmlFor="role-3"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Knowledge manager/enabler"
                  id="role-3"
                  disabled={!companySize}
                />
                <span>Knowledge manager/enabler</span>
              </Label>
              <Label
                htmlFor="role-4"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="IT/Operations"
                  id="role-4"
                  disabled={!companySize}
                />
                <span>IT/Operations</span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Question 3: Knowledge management responsibility */}
      <Card style={{ opacity: role ? 1 : 0.3 }}>
        <CardHeader>
          <CardTitle>
            Who&apos;s responsible for knowledge management at your company?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={knowledgeOwner}
            onValueChange={setKnowledgeOwner}
            disabled={!role}
          >
            <div className="flex flex-col gap-0">
              <Label
                htmlFor="owner-1"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem value="Me" id="owner-1" disabled={!role} />
                <span>Me</span>
              </Label>
              <Label
                htmlFor="owner-2"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem value="My team" id="owner-2" disabled={!role} />
                <span>My team</span>
              </Label>
              <Label
                htmlFor="owner-3"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Dedicated team"
                  id="owner-3"
                  disabled={!role}
                />
                <span>Dedicated team</span>
              </Label>
              <Label
                htmlFor="owner-4"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Don't know / Nobody"
                  id="owner-4"
                  disabled={!role}
                />
                <span>Don&apos;t know / Nobody</span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Question 4: Budget status */}
      <Card style={{ opacity: knowledgeOwner ? 1 : 0.3 }}>
        <CardHeader>
          <CardTitle>Budget status for automation tools this year?</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={budgetStatus}
            onValueChange={setBudgetStatus}
            disabled={!knowledgeOwner}
          >
            <div className="flex flex-col gap-0">
              <Label
                htmlFor="budget-1"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Approved"
                  id="budget-1"
                  disabled={!knowledgeOwner}
                />
                <span>Approved</span>
              </Label>
              <Label
                htmlFor="budget-2"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="In planning"
                  id="budget-2"
                  disabled={!knowledgeOwner}
                />
                <span>In planning</span>
              </Label>
              <Label
                htmlFor="budget-3"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="No budget yet"
                  id="budget-3"
                  disabled={!knowledgeOwner}
                />
                <span>No budget yet</span>
              </Label>
              <Label
                htmlFor="budget-4"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Unsure"
                  id="budget-4"
                  disabled={!knowledgeOwner}
                />
                <span>Unsure</span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Question 5: Decision maker */}
      <Card style={{ opacity: budgetStatus ? 1 : 0.3 }}>
        <CardHeader>
          <CardTitle>Who makes the final call?</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={decisionMaker}
            onValueChange={setDecisionMaker}
            disabled={!budgetStatus}
          >
            <div className="flex flex-col gap-0">
              <Label
                htmlFor="decision-1"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Me"
                  id="decision-1"
                  disabled={!budgetStatus}
                />
                <span>Me</span>
              </Label>
              <Label
                htmlFor="decision-2"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Executive leadership (C-suite/VP)"
                  id="decision-2"
                  disabled={!budgetStatus}
                />
                <span>Executive leadership (C-suite/VP)</span>
              </Label>
              <Label
                htmlFor="decision-3"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Department head/Director"
                  id="decision-3"
                  disabled={!budgetStatus}
                />
                <span>Department head/Director</span>
              </Label>
              <Label
                htmlFor="decision-4"
                className="flex items-center space-x-2 py-2 gap-1 cursor-pointer"
              >
                <RadioGroupItem
                  value="Cross-functional committee"
                  id="decision-4"
                  disabled={!budgetStatus}
                />
                <span>Cross-functional committee</span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      <Button
        className="mt-4"
        disabled={!allFieldsFilled || submit.isSubmitting}
        onClick={submit.submit}
        style={{ background: '#296BF0', color: 'white' }}
      >
        {submit.isSubmitting ? 'Loading...' : 'Continue'}
      </Button>

      {submit.submitError && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">
            Error:{' '}
            {'message' in submit.submitError
              ? submit.submitError.message
              : submit.submitError.error ||
                'Failed to submit survey. Please try again.'}
          </p>
        </div>
      )}
    </div>
  );
}
